/**
 * Quiet combined wind instruments. Angles are clockwise from the bow;
 * the true compass moves with heading while every letter remains upright.
 * The host supplies qualified readings and interpolates between updates.
 */
export const simpleWindDesignNames = Object.freeze({
  clear: "Clear Rose",
  orbit: "Twin Arc",
  petal: "Wind Petals",
});

const C = {
  coral: "#d9775f",
  white: "#f6f6fa",
  muted: "#a0a0a8",
  quiet: "#69696f",
  line: "#38383d",
};
const finite = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const norm = (value) => ((finite(value) % 360) + 360) % 360;
const relative = (value) => ((norm(value) + 180) % 360) - 180;
const f = (value) => finite(value).toFixed(3);
const speed = (value) => {
  const n = Math.max(0, finite(value));
  return n >= 1e6 ? n.toExponential(2) : n.toFixed(1);
};
const heading = (value) =>
  String(Math.round(norm(value)) % 360).padStart(3, "0");
const signed = (value) => {
  const rounded = Math.round(relative(value));
  return `${Math.abs(rounded)}°${rounded < 0 ? " P" : rounded > 0 ? " S" : ""}`;
};
const point = (radius, bearing) => {
  const rad = (finite(bearing) * Math.PI) / 180;
  return [180 + Math.sin(rad) * radius, 142 - Math.cos(rad) * radius];
};
const xy = (position) => position.map(f).join(" ");
const arc = (radius, start, end) =>
  `M ${xy(point(radius, start))} A ${radius} ${radius} 0 0 1 ${xy(point(radius, end))}`;

function compass() {
  return `<g fill="${C.quiet}" text-anchor="middle" dominant-baseline="middle" font-size="12" font-weight="550">
    <text data-bearing="0" x="180" y="22" fill="${C.coral}">N</text>
    <text data-bearing="90" x="300" y="142">E</text>
    <text data-bearing="180" x="180" y="262">S</text>
    <text data-bearing="270" x="60" y="142">W</text>
  </g>
  <path d="M 176 6 L 180 12 L 184 6" fill="none" stroke="${C.white}" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`;
}

function footer() {
  return `<circle cx="91" cy="292" r="3" fill="${C.coral}"/>
    <text x="101" y="296" fill="${C.muted}" font-size="10" font-weight="550">TRUE <tspan data-part="true-angle" fill="${C.white}">—</tspan></text>
    <circle cx="209" cy="292" r="3" fill="${C.white}"/>
    <text x="219" y="296" fill="${C.muted}" font-size="10" font-weight="550">APP <tspan data-part="apparent-angle" fill="${C.white}">—</tspan></text>
    <text x="180" y="329" text-anchor="middle" fill="${C.quiet}" font-size="9">BOW UP · HDG <tspan data-part="heading" fill="${C.muted}">—</tspan>°T</text>`;
}

function centeredSpeeds() {
  return `<text x="180" y="107" text-anchor="middle" fill="${C.muted}" font-size="10" font-weight="550" letter-spacing="1.3">TRUE WIND</text>
    <text data-part="true-speed" x="180" y="152" text-anchor="middle" fill="${C.white}" font-size="43" font-weight="600" letter-spacing="-1.7">—</text>
    <text x="180" y="173" text-anchor="middle" fill="${C.quiet}" font-size="11">knots</text>
    <text x="180" y="201" text-anchor="middle" fill="${C.muted}" font-size="11">Apparent <tspan data-part="apparent-speed" fill="${C.white}" font-weight="550">—</tspan> kn</text>`;
}

function clear() {
  return `<circle cx="180" cy="142" r="97" fill="none" stroke="${C.line}" stroke-width="1.2"/>
    ${compass()}
    <g data-part="true-marker"><path d="M 0 9 L -8 -7 Q 0 -3 8 -7 Z" fill="${C.coral}"/></g>
    <g data-part="apparent-marker"><path d="M 0 7 L -5 -5 L 0 -2 L 5 -5 Z" fill="${C.white}"/></g>
    ${centeredSpeeds()}
    ${footer()}`;
}

function orbit() {
  return `<circle cx="180" cy="142" r="97" fill="none" stroke="${C.line}" stroke-width="1"/>
    ${compass()}
    <path data-part="true-arc" fill="none" stroke="${C.coral}" stroke-width="5" stroke-linecap="round"/>
    <path data-part="apparent-arc" fill="none" stroke="${C.white}" stroke-width="2.5" stroke-linecap="round"/>
    <circle data-part="true-dot" r="4" fill="${C.coral}"/>
    <circle data-part="apparent-dot" r="3" fill="${C.white}"/>
    ${centeredSpeeds()}
    ${footer()}`;
}

function petal() {
  return `<circle cx="180" cy="142" r="97" fill="none" stroke="${C.line}" stroke-width="1" stroke-opacity=".65"/>
    ${compass()}
    <g data-part="true-petal"><path d="M 0 -27 C -18 -40 -25 -71 0 -99 C 25 -71 18 -40 0 -27 Z" fill="${C.coral}" fill-opacity=".22" stroke="${C.coral}" stroke-width="1.3"/></g>
    <g data-part="apparent-petal"><path d="M 0 -25 C -10 -42 -15 -63 0 -82 C 15 -63 10 -42 0 -25 Z" fill="${C.white}" fill-opacity=".10" stroke="${C.white}" stroke-width="1.1"/></g>
    <path d="M 180 123 C 173 136 171 148 173 158 Q 180 163 187 158 C 189 148 187 136 180 123 Z" fill="#1c1c1e" stroke="${C.muted}" stroke-width="1.1"/>
    <text x="110" y="285" text-anchor="middle" fill="${C.coral}" font-size="10" font-weight="550" letter-spacing=".9">TRUE WIND</text>
    <text x="250" y="285" text-anchor="middle" fill="${C.muted}" font-size="10" font-weight="550" letter-spacing=".9">APPARENT</text>
    <text x="110" y="315" text-anchor="middle" fill="${C.white}" font-size="30" font-weight="600" letter-spacing="-1"><tspan data-part="true-speed">—</tspan><tspan fill="${C.quiet}" font-size="11" letter-spacing="0"> kn</tspan></text>
    <text x="250" y="315" text-anchor="middle" fill="${C.white}" font-size="30" font-weight="600" letter-spacing="-1"><tspan data-part="apparent-speed">—</tspan><tspan fill="${C.quiet}" font-size="11" letter-spacing="0"> kn</tspan></text>
    <text x="180" y="334" text-anchor="middle" fill="${C.quiet}" font-size="8.5">TWA <tspan data-part="true-angle" fill="${C.muted}">—</tspan> · AWA <tspan data-part="apparent-angle" fill="${C.muted}">—</tspan> · HDG <tspan data-part="heading" fill="${C.muted}">—</tspan>°T</text>`;
}

/** No paint IDs are needed; every generated SVG is self-contained. */
export function createSimpleWindDesign(variant, uid) {
  if (!Object.hasOwn(simpleWindDesignNames, variant))
    throw new Error(`Unknown simple wind design: ${String(variant)}`);
  const instance = String(uid ?? variant).replace(/[^a-zA-Z0-9_-]/g, "-");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 340" role="img" aria-label="${simpleWindDesignNames[variant]} combined wind instrument" data-simple-wind-design="${variant}" data-instance="${instance}" style="display:block;width:100%;height:auto;overflow:visible;font-family:TCInter,Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-variant-numeric:tabular-nums">${{ clear, orbit, petal }[variant]()}</svg>`;
}

function attr(svg, part, name, value) {
  const node = svg.querySelector(`[data-part="${part}"]`);
  if (node) node.setAttribute(name, String(value));
}
function text(svg, part, value) {
  const node = svg.querySelector(`[data-part="${part}"]`);
  if (node) node.textContent = value;
}
function rotate(svg, part, bearing, radius = 0) {
  attr(
    svg,
    part,
    "transform",
    `translate(180 142) rotate(${f(bearing)}) translate(0 ${-radius})`,
  );
}
function dot(svg, part, radius, bearing) {
  const [x, y] = point(radius, bearing);
  attr(svg, part, "cx", f(x));
  attr(svg, part, "cy", f(y));
}

/**
 * Draw one interpolated frame. No timers, telemetry generation, or history trails.
 * The host handles missing/stale telemetry; defensive geometry remains finite.
 */
export function updateSimpleWindDesign(
  svg,
  variant,
  reading = {},
  _history = [],
) {
  if (!Object.hasOwn(simpleWindDesignNames, variant))
    throw new Error(`Unknown simple wind design: ${String(variant)}`);
  const hdg = norm(reading.heading);
  const twa = relative(reading.trueAngle);
  const awa = relative(reading.apparentAngle);
  const tws = speed(reading.trueSpeed);
  const aws = speed(reading.apparentSpeed);
  text(svg, "true-speed", tws);
  text(svg, "apparent-speed", aws);
  text(svg, "heading", heading(hdg));
  text(svg, "true-angle", signed(twa));
  text(svg, "apparent-angle", signed(awa));
  for (const letter of svg.querySelectorAll("[data-bearing]")) {
    const [x, y] = point(120, Number(letter.dataset.bearing) - hdg);
    letter.setAttribute("x", f(x));
    letter.setAttribute("y", f(y));
  }
  if (variant === "clear") {
    rotate(svg, "true-marker", twa, 97);
    rotate(svg, "apparent-marker", awa, 80);
  } else if (variant === "orbit") {
    attr(svg, "true-arc", "d", arc(97, twa - 32, twa));
    attr(svg, "apparent-arc", "d", arc(79, awa - 26, awa));
    dot(svg, "true-dot", 97, twa);
    dot(svg, "apparent-dot", 79, awa);
  } else {
    rotate(svg, "true-petal", twa);
    rotate(svg, "apparent-petal", awa);
  }
  svg.setAttribute(
    "aria-label",
    `${simpleWindDesignNames[variant]}: true wind ${tws} knots, ${signed(twa)} relative to bow; apparent wind ${aws} knots, ${signed(awa)} relative to bow; heading ${heading(hdg)} degrees true. Bow up.`,
  );
}
